import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Heart, Star, User } from "lucide-react";
import DecorativeDivider from "@/components/DecorativeDivider";
import PhotoCarousel from "@/components/PhotoCarousel";
import standingImage from "@/assets/standing.jpg";

const ORIGINAL_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/j4y9u5jg_Hero%201.JPG";
const CASUAL_IMAGE = "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/5zc504jh_Hero%202.JPG";

const personalPhotos = [
  {
    src: CASUAL_IMAGE,
    alt: "Nishad at home",
    aspect: "4/5",
    position: "center",
  },
  {
    src: ORIGINAL_HERO_IMAGE,
    alt: "Nishad Ramnath Dangui",
    aspect: "4/5",
    position: "center",
  },
  {
    src: standingImage,
    alt: "Nishad Dangui",
    aspect: "4/5",
    position: "top center",
  },
];

const details = [
  { label: "Date of Birth", value: "20 December 1995", icon: Calendar },
  { label: "Age", value: "30", icon: User },
  { label: "Height", value: "5'6\"", icon: User },
  { label: "Weight", value: "60 kg", icon: User },
  { label: "Religion", value: "Hindu", icon: Star },
  { label: "Caste", value: "Vaishya Vani", icon: Star },
  { label: "Gotra", value: "Gargya", icon: Star },
  { label: "Mother Tongue", value: "Konkani", icon: Heart },
  { label: "Marital Status", value: "Single", icon: Heart },
  { label: "Horoscope", value: "Scorpio", icon: Star },
  { label: "Current Location", value: "Goa", icon: MapPin },
];

export default function PersonalDetails() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 md:py-16 px-4 sm:px-6 md:px-12"
      data-testid="personal-details-section"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p
            className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 font-semibold"
            style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
          >
            About
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6 md:mb-8"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Personal Details
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 items-start">
          {/* Photo Column */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={visible ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-2 max-w-sm mx-auto lg:max-w-none w-full"
          >
            <PhotoCarousel photos={personalPhotos} />
          </motion.div>

          {/* Details Grid */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 sm:gap-x-8 gap-y-3.5 sm:gap-y-4">
              {details.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={visible ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }}
                    className="flex items-start gap-3 pb-3 sm:pb-3.5"
                    style={{ borderBottom: "1px solid #E6E2DD" }}
                  >
                    <Icon
                      className="w-4 h-4 mt-1 flex-shrink-0"
                      style={{ color: "#8A9A5B" }}
                      strokeWidth={1.5}
                    />
                    <div>
                      <p
                        className="text-xs tracking-wide uppercase mb-0.5 font-medium"
                        style={{ color: "#999999", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.label}
                      </p>
                      <p
                        className="text-sm sm:text-base font-normal"
                        style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.value}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-8 sm:mt-10">
          <DecorativeDivider />
        </div>
      </div>
    </section>
  );
}
